import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useId,
  type ComponentType,
  type SVGProps,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import {
  MapPinIcon,
  ChevronDownIcon,
  SearchIcon,
  FacilitiesIcon,
  DeviceIcon,
  MembersIcon,
  DocumentsIcon,
  ListChecksIcon,
  ClipboardCheckIcon,
  PackageIcon,
  TruckIcon,
} from "@/components/icons";
import { useDocumentScopeSearch } from "@/hooks/use-document-scope-search";

type DocumentNodeType = components["schemas"]["DocumentNodeType"];
type DocumentSource = components["schemas"]["DocumentSource"];
type DocumentScopeMatch = components["schemas"]["DocumentScopeMatch"];

/** A picked entity scope. `rolls_up` is true for facilities (their doc_count
 *  aggregates devices / sampling / direct). */
export interface DocumentScope {
  node_type: DocumentNodeType;
  node_id: string;
  label: string;
  rolls_up: boolean;
}

/** The picker emits a discriminated result: an entity scope (node axis), a
 *  source filter (Tasks — NOT node axes), or the org-wide "all". */
export type ScopeChange =
  | { kind: "entity"; scope: DocumentScope }
  | { kind: "source"; source: DocumentSource }
  | { kind: "all" };

interface DocumentScopePickerProps {
  value: DocumentScope | null;
  onChange: (change: ScopeChange) => void;
}

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

// The entity scope axes the scope-search returns, in render order. The first
// three (facility / device_installation / account) are premise/roll-up axes;
// the last four (org_tester / field_credential / test_kit / testing_company)
// are context-anchored groups — document-registry entities that carry their
// own documents (evidence, credential files, cert docs) but never roll up to
// anything else. Other DocumentNodeType values never appear in a match and are
// not rendered.
const ENTITY_GROUPS: DocumentNodeType[] = [
  "facility",
  "device_installation",
  "account",
  "org_tester",
  "field_credential",
  "test_kit",
  "testing_company",
];

// Per-entity glyph chip: colored square + the entity's inline SVG (NO emoji).
// Colors mirror mockup 05 (blue facility / violet device / cyan account); these
// are entity-identity colors, distinct from the configurable primary accent.
function entityGlyph(nodeType: DocumentNodeType): { chip: string; Icon: IconComponent } {
  switch (nodeType) {
    case "facility":
      return { chip: "bg-blue-600", Icon: FacilitiesIcon };
    case "device_installation":
      return { chip: "bg-violet-600", Icon: DeviceIcon };
    case "account":
      return { chip: "bg-cyan-600", Icon: MembersIcon };
    case "org_tester":
      return { chip: "bg-emerald-600", Icon: MembersIcon };
    case "field_credential":
      return { chip: "bg-amber-600", Icon: ClipboardCheckIcon };
    case "test_kit":
      return { chip: "bg-rose-600", Icon: PackageIcon };
    case "testing_company":
      return { chip: "bg-indigo-600", Icon: TruckIcon };
    default:
      return { chip: "bg-gray-500", Icon: DocumentsIcon };
  }
}

// Flattened, keyboard-navigable option model. Quick scopes first, then the
// grouped entity matches.
type FlatOption =
  | { key: string; kind: "all" }
  | { key: string; kind: "source"; source: DocumentSource }
  | { key: string; kind: "entity"; match: DocumentScopeMatch };

const QUICK_OPTIONS: FlatOption[] = [
  { key: "quick-all", kind: "all" },
  { key: "quick-task", kind: "source", source: "task" },
];

function quickGlyph(option: Extract<FlatOption, { kind: "all" | "source" }>): IconComponent {
  if (option.kind === "all") return DocumentsIcon;
  return ListChecksIcon;
}

function quickLabelKey(option: Extract<FlatOption, { kind: "all" | "source" }>): string {
  if (option.kind === "all") return "repo.picker.quick.all";
  return "repo.picker.quick.task";
}

interface Rect {
  top: number;
  left: number;
}

const POPOVER_WIDTH = 340;

/**
 * The omni-scope type-ahead — the document tree's replacement (mockup 05 Stage
 * A). A blue Scope pill opens a portaled popover: a debounced search input over
 * Quick scopes (All / Tasks) + grouped entity matches (Facilities / Devices /
 * Accounts / Testers / Field Credentials / Test Kits / Testing Companies),
 * each row a colored glyph + name + roll-up hint + rolled-up doc
 * count. Keyboard: arrow / enter / escape.
 */
export function DocumentScopePicker({ value, onChange }: DocumentScopePickerProps) {
  const { t } = useTranslation("documents");
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const [rawQuery, setRawQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState<Rect | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the typed query before it reaches the network; the hook is enabled
  // only while the picker is open (closed → undefined → disabled query).
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(rawQuery), 250);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  const { data, isLoading } = useDocumentScopeSearch(open ? debouncedQuery : undefined);
  const matches = data?.matches ?? [];

  const groups = ENTITY_GROUPS.map((nodeType) => ({
    nodeType,
    options: matches
      .filter((m) => m.node_type === nodeType)
      .map<FlatOption>((m) => ({ key: `${m.node_type}:${m.node_id}`, kind: "entity", match: m })),
  })).filter((g) => g.options.length > 0);

  const flatOptions: FlatOption[] = [...QUICK_OPTIONS, ...groups.flatMap((g) => g.options)];
  const indexByKey = new Map(flatOptions.map((o, i) => [o.key, i]));

  // Position the portaled popover under the trigger. Recompute on open + while
  // the page scrolls / resizes (mirrors the app's Combobox).
  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom, left: r.left });
  }, []);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const handler = () => measure();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, measure]);

  // Focus the search input when the popover opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close on outside click. The popover is portaled OUTSIDE containerRef, so
  // treat clicks inside it as inside too.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target) ?? false;
      const inPopover = popoverRef.current?.contains(target) ?? false;
      if (!inContainer && !inPopover) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset the active row whenever the option set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [flatOptions.length]);

  const closeAndReset = useCallback(() => {
    setOpen(false);
    setRawQuery("");
  }, []);

  const select = useCallback(
    (option: FlatOption) => {
      if (option.kind === "all") {
        onChange({ kind: "all" });
      } else if (option.kind === "source") {
        onChange({ kind: "source", source: option.source });
      } else {
        onChange({
          kind: "entity",
          scope: {
            node_type: option.match.node_type,
            node_id: option.match.node_id,
            label: option.match.label,
            rolls_up: option.match.node_type === "facility",
          },
        });
      }
      closeAndReset();
    },
    [onChange, closeAndReset],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < flatOptions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : flatOptions.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = flatOptions[activeIndex];
      if (option) select(option);
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndReset();
    }
  };

  // Keep the active row scrolled into view.
  useEffect(() => {
    if (activeIndex < 0 || !popoverRef.current) return;
    const el = popoverRef.current.querySelector(`[data-opt-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const renderOption = (option: FlatOption) => {
    const idx = indexByKey.get(option.key) ?? -1;
    const isActive = idx === activeIndex;
    const isSelected =
      option.kind === "entity" &&
      value?.node_type === option.match.node_type &&
      value?.node_id === option.match.node_id;

    let glyphChip: string;
    let GlyphIcon: IconComponent;
    let label: string;
    let rollsUp = false;
    let count: number | null = null;

    if (option.kind === "entity") {
      const glyph = entityGlyph(option.match.node_type);
      glyphChip = glyph.chip;
      GlyphIcon = glyph.Icon;
      label = option.match.label;
      rollsUp = option.match.node_type === "facility";
      count = option.match.doc_count;
    } else {
      glyphChip = "bg-gray-500";
      GlyphIcon = quickGlyph(option);
      label = t(quickLabelKey(option));
    }

    return (
      <div
        key={option.key}
        role="option"
        aria-selected={isActive}
        data-opt-index={idx}
        onClick={() => select(option)}
        onMouseEnter={() => setActiveIndex(idx)}
        className={`flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm ${
          isActive ? "bg-primary/10" : "hover:bg-gray-50 dark:hover:bg-gray-700"
        }`}
      >
        <span
          className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] text-white ${glyphChip}`}
        >
          <GlyphIcon className="h-3 w-3" />
        </span>
        <span
          className={`flex flex-1 items-center gap-1 truncate ${
            isActive || isSelected ? "text-primary" : "text-on-surface"
          }`}
        >
          <span className="truncate">{label}</span>
          {rollsUp && (
            <span className="flex-none text-xs text-muted">{t("repo.picker.inclDevices")}</span>
          )}
        </span>
        {count !== null && (
          <span className="flex-none text-xs font-semibold text-muted">
            {count.toLocaleString()}
          </span>
        )}
      </div>
    );
  };

  const popover =
    open && rect !== null
      ? createPortal(
          <div
            ref={popoverRef}
            style={{ top: rect.top + 4, left: rect.left, width: POPOVER_WIDTH }}
            className="fixed z-[60] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2.5 dark:border-gray-700">
              <SearchIcon className="h-4 w-4 flex-none text-gray-400" />
              <input
                ref={inputRef}
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                autoComplete="off"
                value={rawQuery}
                placeholder={t("repo.picker.searchPlaceholder")}
                onChange={(e) => setRawQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent text-sm text-on-surface placeholder:text-muted focus:outline-none"
              />
            </div>
            <div id={listboxId} role="listbox" className="max-h-[320px] overflow-y-auto py-1">
              <div className="px-3 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-wider text-muted">
                {t("repo.picker.groups.quick")}
              </div>
              {QUICK_OPTIONS.map(renderOption)}
              {groups.map((group) => (
                <div key={group.nodeType}>
                  <div className="px-3 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-wider text-muted">
                    {t(`repo.picker.groups.${group.nodeType}`)}
                  </div>
                  {group.options.map(renderOption)}
                </div>
              ))}
              {isLoading && (
                <div className="px-3 py-2 text-xs text-muted">{t("repo.picker.loading")}</div>
              )}
              {!isLoading && groups.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted">{t("repo.picker.empty")}</div>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"
      >
        <MapPinIcon className="h-3.5 w-3.5 text-primary/60" />
        {value?.label ?? t("repo.picker.trigger")}
        <ChevronDownIcon className="h-3 w-3 text-primary/60" />
      </button>
      {popover}
    </div>
  );
}
